import { useEffectOnce, useLoadableState } from '@carvana/merch-react-hooks';
import { isSuccess, Result } from '@carvana/merch-result';
import { useContext } from 'react';
import { ImageWrapper } from '@/home/HeroCardsReturnUser/Cards/TradeCard/Details.styles';
import { axios } from '~/clients';
import { getBlueSteel } from '~/constants/endpoints';
import StaticContext from '~/context/common/StaticContext';
import TradeContext from '~/context/common/TradeContext';
import { BluesteelResponse } from '~/pages/api/bluesteel/v1/get';
import { generateErrorLogger } from '~/utilities/loggers';

type UrlParams = {
  year: number;
  make: string;
  model: string;
  trim: string;
  color: string;
};

async function fetchImageUrl(
  { year, make, model, trim, color }: UrlParams,
  correlationId: string
): Promise<string> {
  const url = getBlueSteel({ currentPageUrl: window.location.href });
  const query = new URLSearchParams({
    year: String(year),
    make,
    model,
    trim,
    color
  }).toString();

  const { data } = await axios.get<Result<BluesteelResponse>>(
    `${url}?${query}`,
    {
      headers: {
        'x-client-id': 'e902dfb1-f373-450d-8cca-9db2e12bfce5',
        'x-correlation-id': correlationId
      }
    }
  );

  if (isSuccess(data)) {
    return data.content.bluesteelImageUrl;
  }

  return '';
}

type OwnProps = {
  consumer: 'control' | 'experiment';
};

export default function TradeImage({ consumer }: OwnProps) {
  const { eventCorrelationId } = useContext(StaticContext);
  const {
    statusAgnosticTrade,
    trades: { isLoaded: isTradeLoaded }
  } = useContext(TradeContext);
  const {
    data: imageUrl,
    isLoaded,
    setData,
    setError
  } = useLoadableState<string>('');
  const { vehicle } = statusAgnosticTrade ?? {};
  const showImage = isLoaded && Boolean(imageUrl);

  const controlVehicleImageClassName =
    'absolute right-[-20px] w-[130px] m:w-[166px]';
  const experimentVehicleImageClassName =
    '!h-[120px] !w-[279px] self-center rounded-md bg-grey-1 object-cover m:max-h-[72px] m:max-w-[72px]';

  useEffectOnce(() => {
    if (!isTradeLoaded || !statusAgnosticTrade || !vehicle) return;

    fetchImageUrl(vehicle, eventCorrelationId)
      .then(setData)
      .catch(generateErrorLogger('TradeImage', {}, { callback: setError }));
  }, [
    eventCorrelationId,
    imageUrl,
    isLoaded,
    isTradeLoaded,
    setData,
    setError,
    statusAgnosticTrade,
    vehicle
  ]);

  if (consumer === 'control') {
    return showImage ? (
      <img
        className={controlVehicleImageClassName}
        src={imageUrl}
        alt="Trade-in vehicle"
      />
    ) : null;
  }

  if (consumer === 'experiment') {
    return showImage ? (
      <ImageWrapper>
        <img
          className={experimentVehicleImageClassName}
          src={imageUrl}
          alt="Trade-in vehicle"
        />
      </ImageWrapper>
    ) : null;
  }

  return null;
}
